import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatEastern } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  LayoutDashboard, Calendar, FileText, Image as ImageIcon, Users, ShoppingBag, Star,
  Mail, Inbox, Settings as SettingsIcon, Menu, Loader2, Search, Download,
  Building2, CreditCard, RefreshCw, Send, Undo2,
} from "lucide-react";

// Mirror Admin.tsx nav (with Customers added)
const adminNav = [
  { label: "Dashboard", to: "/admin", icon: LayoutDashboard },
  { label: "Bookings", to: "/admin/bookings", icon: Calendar },
  { label: "Customers", to: "/admin/customers", icon: Users },
  { label: "Studios", to: "/admin/studios", icon: Building2 },
  { label: "Email Log", to: "/admin/emails", icon: Send },
  { label: "Stripe Sync", to: "/admin/stripe/sync", icon: RefreshCw },
  { label: "Stripe Events", to: "/admin/stripe/events", icon: CreditCard },
  { label: "Blog", to: "/admin/blog", icon: FileText },
  { label: "Gallery", to: "/admin/gallery", icon: ImageIcon },
  { label: "Team", to: "/admin/team", icon: Users },
  { label: "Products", to: "/admin/products", icon: ShoppingBag },
  { label: "Testimonials", to: "/admin/testimonials", icon: Star },
  { label: "Subscribers", to: "/admin/subscribers", icon: Mail },
  { label: "Contact", to: "/admin/contact", icon: Inbox },
  { label: "Settings", to: "/admin/settings", icon: SettingsIcon },
  { label: "Import from WP", to: "/admin/wp-import", icon: Download },
  { label: "Refund Policies", to: "/admin/refund-policies", icon: Undo2 },
];

function Sidebar({ open, toggle }: { open: boolean; toggle: () => void }) {
  const location = useLocation();
  return (
    <>
      <button onClick={toggle} className="lg:hidden fixed top-20 left-4 z-40 bg-panel border border-border p-2">
        <Menu className="w-5 h-5" />
      </button>
      <aside className={`fixed top-16 left-0 bottom-0 w-64 bg-surface border-r border-border z-30 transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <nav className="p-4 space-y-1 mt-4">
          {adminNav.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`flex items-center gap-3 px-4 py-2.5 text-sm font-body transition-colors ${
                location.pathname === item.to ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-panel"
              }`}
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}
        </nav>
      </aside>
    </>
  );
}

type Customer = {
  id: string;
  full_name: string | null;
  email: string;
  phone: string | null;
  notes: string | null;
  created_at: string;
};

type BookingLite = {
  id: string; start_at: string; end_at: string; total: number; status: string;
  payment_status: string; studio_id: string;
};
type OrderLite = {
  id: string; created_at: string; total: number; status: string; payment_status: string;
};

const fmtMoney = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n || 0);

export default function AdminCustomers() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<Customer[]>([]);
  const [counts, setCounts] = useState<Record<string, { bookings: number; orders: number; spend: number }>>({});
  const [search, setSearch] = useState("");
  const [openCustomer, setOpenCustomer] = useState<Customer | null>(null);
  const [bookings, setBookings] = useState<BookingLite[]>([]);
  const [orders, setOrders] = useState<OrderLite[]>([]);
  const [studios, setStudios] = useState<Record<string, string>>({});
  const [detailLoading, setDetailLoading] = useState(false);

  // Edit/create
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState<Partial<Customer>>({});

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("customers")
      .select("id,full_name,email,phone,notes,created_at")
      .order("created_at", { ascending: false })
      .limit(2000);
    if (error) toast.error(error.message);
    const list = (data ?? []) as Customer[];
    setRows(list);

    // Bulk-load booking & order counts
    const ids = list.map((c) => c.id);
    const emails = list.map((c) => c.email.toLowerCase());
    const [{ data: b }, { data: o }] = await Promise.all([
      ids.length
        ? supabase.from("bookings").select("customer_id,total").in("customer_id", ids)
        : Promise.resolve({ data: [] as any[] }),
      emails.length
        ? supabase.from("orders").select("customer_email,total").in("customer_email", emails)
        : Promise.resolve({ data: [] as any[] }),
    ]);
    const map: Record<string, { bookings: number; orders: number; spend: number }> = {};
    for (const c of list) map[c.id] = { bookings: 0, orders: 0, spend: 0 };
    for (const row of (b ?? []) as any[]) {
      const m = map[row.customer_id];
      if (m) { m.bookings++; m.spend += Number(row.total ?? 0); }
    }
    const byEmail: Record<string, string> = {};
    for (const c of list) byEmail[c.email.toLowerCase()] = c.id;
    for (const row of (o ?? []) as any[]) {
      const cid = byEmail[String(row.customer_email ?? "").toLowerCase()];
      const m = cid ? map[cid] : undefined;
      if (m) { m.orders++; m.spend += Number(row.total ?? 0); }
    }
    setCounts(map);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    supabase.from("studios").select("id,name").then(({ data }) => {
      const m: Record<string, string> = {};
      for (const s of (data ?? []) as { id: string; name: string }[]) m[s.id] = s.name;
      setStudios(m);
    });
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((c) =>
      (c.full_name ?? "").toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.phone ?? "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  const openDetail = async (c: Customer) => {
    setOpenCustomer(c);
    setDetailLoading(true);
    setBookings([]); setOrders([]);
    const [{ data: b }, { data: o }] = await Promise.all([
      supabase
        .from("bookings")
        .select("id,start_at,end_at,total,status,payment_status,studio_id")
        .eq("customer_id", c.id)
        .order("start_at", { ascending: false }),
      supabase
        .from("orders")
        .select("id,created_at,total,status,payment_status")
        .ilike("customer_email", c.email)
        .order("created_at", { ascending: false }),
    ]);
    setBookings((b ?? []) as BookingLite[]);
    setOrders((o ?? []) as OrderLite[]);
    setDetailLoading(false);
  };

  const startEdit = (c?: Customer) => {
    setDraft(c ? { ...c } : { full_name: "", email: "", phone: "", notes: "" });
    setEditOpen(true);
  };

  const saveCustomer = async () => {
    const email = (draft.email ?? "").trim().toLowerCase();
    if (!email) return toast.error("Email required");
    if (draft.id) {
      const { error } = await supabase
        .from("customers")
        .update({
          full_name: draft.full_name ?? null,
          email,
          phone: draft.phone ?? null,
          notes: draft.notes ?? null,
        })
        .eq("id", draft.id);
      if (error) return toast.error(error.message);
    } else {
      const { error } = await supabase
        .from("customers")
        .insert({
          full_name: draft.full_name ?? null,
          email,
          phone: draft.phone ?? null,
          notes: draft.notes ?? null,
        });
      if (error) return toast.error(error.message);
    }
    toast.success("Customer saved");
    setEditOpen(false);
    load();
  };

  return (
    <div className="pt-16 min-h-screen">
      <Sidebar open={sidebarOpen} toggle={() => setSidebarOpen(!sidebarOpen)} />
      <div className="lg:ml-64 p-6 lg:p-10">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <h1 className="text-3xl font-display tracking-widest">CUSTOMERS</h1>
          <Button onClick={() => startEdit()}>+ New customer</Button>
        </div>

        <div className="flex items-center gap-2 mb-4 max-w-md">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search name, email, phone…"
              className="pl-9"
            />
          </div>
          <p className="text-xs text-muted-foreground whitespace-nowrap">
            {filtered.length} / {rows.length}
          </p>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading customers…
          </div>
        ) : (
          <div className="border border-border overflow-hidden">
            <div className="grid grid-cols-12 gap-3 px-4 py-2 bg-surface text-xs uppercase tracking-wider text-muted-foreground">
              <div className="col-span-4">Name</div>
              <div className="col-span-4">Email</div>
              <div className="col-span-1 text-right">Bookings</div>
              <div className="col-span-1 text-right">Orders</div>
              <div className="col-span-2 text-right">Lifetime $</div>
            </div>
            <div className="divide-y divide-border max-h-[70vh] overflow-auto">
              {filtered.map((c) => {
                const k = counts[c.id] ?? { bookings: 0, orders: 0, spend: 0 };
                return (
                  <button
                    key={c.id}
                    onClick={() => openDetail(c)}
                    className="w-full grid grid-cols-12 gap-3 px-4 py-2 items-center text-sm hover:bg-panel text-left"
                  >
                    <div className="col-span-4 truncate font-body">{c.full_name || <span className="text-muted-foreground italic">—</span>}</div>
                    <div className="col-span-4 truncate font-mono text-xs">{c.email}</div>
                    <div className="col-span-1 text-right">{k.bookings}</div>
                    <div className="col-span-1 text-right">{k.orders}</div>
                    <div className="col-span-2 text-right">{fmtMoney(k.spend)}</div>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="px-4 py-8 text-center text-sm text-muted-foreground">No customers match.</div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!openCustomer} onOpenChange={(o) => !o && setOpenCustomer(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>{openCustomer?.full_name || openCustomer?.email}</DialogTitle>
          </DialogHeader>
          {openCustomer && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Email</p>
                  <p className="font-mono">{openCustomer.email}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Phone</p>
                  <p>{openCustomer.phone || "—"}</p>
                </div>
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Created</p>
                  <p>{new Date(openCustomer.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex items-end">
                  <Button size="sm" variant="outline" onClick={() => { setOpenCustomer(null); startEdit(openCustomer); }}>
                    Edit
                  </Button>
                </div>
              </div>

              {openCustomer.notes && (
                <div>
                  <p className="text-xs uppercase text-muted-foreground">Notes</p>
                  <p className="whitespace-pre-wrap">{openCustomer.notes}</p>
                </div>
              )}

              <div>
                <h3 className="text-sm font-display uppercase tracking-wider mb-2">
                  Bookings ({bookings.length})
                </h3>
                {detailLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : bookings.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No bookings.</p>
                ) : (
                  <div className="border border-border divide-y divide-border">
                    {bookings.map((b) => (
                      <div key={b.id} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs items-center">
                        <div className="col-span-4">{formatEastern(b.start_at)}</div>
                        <div className="col-span-3 truncate">{studios[b.studio_id] ?? "Studio"}</div>
                        <div className="col-span-2 capitalize">{b.status}</div>
                        <div className="col-span-2 capitalize">{b.payment_status}</div>
                        <div className="col-span-1 text-right">{fmtMoney(Number(b.total))}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <h3 className="text-sm font-display uppercase tracking-wider mb-2">
                  Orders ({orders.length})
                </h3>
                {detailLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : orders.length === 0 ? (
                  <p className="text-xs text-muted-foreground">No orders.</p>
                ) : (
                  <div className="border border-border divide-y divide-border">
                    {orders.map((o) => (
                      <div key={o.id} className="px-3 py-2 grid grid-cols-12 gap-2 text-xs items-center">
                        <div className="col-span-4">{new Date(o.created_at).toLocaleString()}</div>
                        <div className="col-span-3 truncate font-mono">{o.id.slice(0, 8)}</div>
                        <div className="col-span-2 capitalize">{o.status}</div>
                        <div className="col-span-2 capitalize">{o.payment_status}</div>
                        <div className="col-span-1 text-right">{fmtMoney(Number(o.total))}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit/create dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{draft.id ? "Edit customer" : "New customer"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Full name</Label>
              <Input
                value={draft.full_name ?? ""}
                onChange={(e) => setDraft({ ...draft, full_name: e.target.value })}
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={draft.email ?? ""}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={draft.phone ?? ""}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                rows={3}
                value={draft.notes ?? ""}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={saveCustomer}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}